import BASE_URL from "../../config/api";
import axios from "axios";
import { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams, Link } from "react-router-dom";
import { Upload, Form } from "antd";
import { BsCloudUpload, BsCheckCircle, BsArrowLeft, BsImage, BsTrash, BsPlayCircle, BsPlus } from "react-icons/bs";
const { Dragger } = Upload;

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 20 }, (_, i) => String(CURRENT_YEAR - i));

const toEmbedUrl = (url) => {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url;
};

const AddPicture = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [pictures, setPictures] = useState([]);
  const [loadingProject, setLoadingProject] = useState(true);
  const [formFields, setFormFields] = useState({ picture: "", details: "" });
  const [fileName, setFileName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Project info editing
  const [editFields, setEditFields] = useState({ title: "", description: "", location: "", status: "Ongoing", clientName: "", year: String(CURRENT_YEAR) });
  const [isSavingInfo, setIsSavingInfo] = useState(false);

  // Videos
  const [videoFileName, setVideoFileName] = useState("");
  const [videoUrl, setVideoUrl] = useState("");

  const loadProject = async () => {
    setLoadingProject(true);
    try {
      const res = await axios.get(`${BASE_URL}/construct/get-construction/${id}`);
      const data = res.data.data;
      setProject(data);
      setPictures(data.pictures || []);
      setEditFields({
        title: data.title || "",
        description: data.description || "",
        location: data.location || "",
        status: data.status || "Ongoing",
        clientName: data.clientName || "",
        year: data.year || String(CURRENT_YEAR),
      });
    } catch {
      toast.error("Failed to load project data");
    } finally {
      setLoadingProject(false);
    }
  };

  useEffect(() => { loadProject(); }, [id]);

  const handleUpload = (file) => {
    if (file.file.status === "done") {
      const response = file.file.response;
      if (response?.success) {
        setFormFields((prev) => ({ ...prev, picture: response.data[0].bucket.data.url }));
        setFileName(file.file.name);
        toast.success("Image uploaded");
      } else {
        setFileName("");
        toast.error("Upload failed");
      }
    } else if (file.file.status === "error") {
      setFileName("");
      toast.error("Upload failed — network issue");
    }
  };

  const authHeaders = { Authorization: `Bearer ${localStorage.getItem("userToken")}` };

  const uploadConfig = { name: "file", maxCount: 1, action: `${BASE_URL}/bucket/upload`, headers: authHeaders, onChange: handleUpload, showUploadList: false };

  const submit = async () => {
    if (!formFields.picture) { toast.error("Please upload a picture first"); return; }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${BASE_URL}/picture/add-picture`, { ...formFields, drawingId: id }, { headers: authHeaders });
      if (response.status === 200) {
        toast.success("Photo added");
        setFormFields({ picture: "", details: "" });
        setFileName("");
        setTimeout(loadProject, 800);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Submission failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveProjectInfo = async () => {
    if (!editFields.title.trim()) { toast.error("Title is required"); return; }
    setIsSavingInfo(true);
    try {
      await axios.put(`${BASE_URL}/construct/update-construction/${id}`, editFields, { headers: authHeaders });
      toast.success("Project info updated");
      loadProject();
    } catch {
      toast.error("Failed to update project info");
    } finally {
      setIsSavingInfo(false);
    }
  };

  const handleVideoUpload = (file) => {
    if (file.file.status === "uploading") {
      setVideoFileName("Uploading…");
    } else if (file.file.status === "done") {
      const response = file.file.response;
      if (response?.success) {
        const url = response.data[0].bucket.data.secure_url;
        setVideoUrl(url);
        setVideoFileName(file.file.name);
        toast.success("Video uploaded — click Add Video to save");
      } else {
        setVideoFileName("");
        toast.error("Upload failed");
      }
    } else if (file.file.status === "error") {
      setVideoFileName("");
      toast.error("Upload failed — network issue");
    }
  };

  const videoUploadConfig = {
    name: "file",
    maxCount: 1,
    action: `${BASE_URL}/bucket/upload-video`,
    headers: authHeaders,
    onChange: handleVideoUpload,
    showUploadList: false,
    accept: "video/*",
  };

  const addVideo = async () => {
    if (!videoUrl) return;
    try {
      const currentVideos = project?.videos || [];
      await axios.put(`${BASE_URL}/construct/update-construction/${id}`, {
        ...editFields,
        videos: [...currentVideos, videoUrl],
      }, { headers: authHeaders });
      setVideoUrl("");
      setVideoFileName("");
      toast.success("Video added");
      loadProject();
    } catch {
      toast.error("Failed to save video");
    }
  };

  const removeVideo = async (index) => {
    const updated = (project?.videos || []).filter((_, i) => i !== index);
    try {
      await axios.put(`${BASE_URL}/construct/update-construction/${id}`, { ...editFields, videos: updated }, { headers: authHeaders });
      toast.success("Video removed");
      loadProject();
    } catch {
      toast.error("Failed to remove video");
    }
  };

  const inputClass = "w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1877F2]/30 focus:border-[#1877F2] transition-colors";
  const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";

  return (
    <div>
      <ToastContainer />

      <div className="mb-6">
        <Link to="/all-constructs" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-800 text-sm font-medium transition-colors">
          <BsArrowLeft size={16} /> Back to Constructions
        </Link>
      </div>

      {loadingProject ? (
        <div className="mb-8 animate-pulse space-y-2">
          <div className="h-6 bg-gray-200 rounded w-48" />
          <div className="h-4 bg-gray-200 rounded w-32" />
        </div>
      ) : (
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{project?.title || "Edit Project"}</h1>
          <p className="text-gray-500 text-sm mt-1">{pictures.length} photo{pictures.length !== 1 ? "s" : ""} · {project?.videos?.length || 0} video{(project?.videos?.length || 0) !== 1 ? "s" : ""}</p>
        </div>
      )}

      <div className="max-w-3xl space-y-6">

        {/* ── Project Info ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Project Info</h2>

          <div>
            <label className={labelClass}>Title *</label>
            <input type="text" value={editFields.title} onChange={(e) => setEditFields(p => ({ ...p, title: e.target.value }))} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea rows={3} value={editFields.description} onChange={(e) => setEditFields(p => ({ ...p, description: e.target.value }))} className={`${inputClass} resize-none`} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Location</label>
              <input type="text" value={editFields.location} onChange={(e) => setEditFields(p => ({ ...p, location: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Client Name</label>
              <input type="text" value={editFields.clientName} onChange={(e) => setEditFields(p => ({ ...p, clientName: e.target.value }))} className={inputClass} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Status</label>
              <select value={editFields.status} onChange={(e) => setEditFields(p => ({ ...p, status: e.target.value }))} className={inputClass}>
                <option value="Ongoing">Ongoing</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Year</label>
              <select value={editFields.year} onChange={(e) => setEditFields(p => ({ ...p, year: e.target.value }))} className={inputClass}>
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <button onClick={saveProjectInfo} disabled={isSavingInfo}
            className="bg-[#1877F2] hover:bg-[#0e5fc7] text-white font-bold px-6 py-2.5 rounded-xl text-sm transition-colors disabled:opacity-60">
            {isSavingInfo ? "Saving…" : "Save Changes"}
          </button>
        </div>

        {/* ── Videos ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Videos</h2>
          <p className="text-xs text-gray-400">Upload a video file — it will be stored on Cloudinary</p>

          <Dragger {...videoUploadConfig}
            className="!border-dashed !border-gray-300 !rounded-xl !bg-gray-50 hover:!border-[#1877F2] transition-colors">
            {videoFileName ? (
              <div className="py-5 flex flex-col items-center gap-2">
                <BsCheckCircle size={28} className={videoFileName === "Uploading…" ? "text-gray-400 animate-pulse" : "text-green-500"} />
                <p className="text-sm font-semibold text-gray-700">{videoFileName}</p>
                <p className="text-xs text-gray-400">Click or drag to replace</p>
              </div>
            ) : (
              <div className="py-7 flex flex-col items-center gap-2">
                <BsPlayCircle size={32} className="text-gray-400" />
                <p className="text-sm font-semibold text-gray-600">Click or drag a video to upload</p>
                <p className="text-xs text-gray-400">MP4, MOV, AVI, WebM</p>
              </div>
            )}
          </Dragger>

          {videoUrl && (
            <div className="flex items-center justify-between gap-3">
              <video src={videoUrl} controls className="flex-1 rounded-xl max-h-40" />
              <button onClick={addVideo}
                className="flex items-center gap-1.5 bg-[#1877F2] hover:bg-[#0e5fc7] text-white font-bold px-4 py-2.5 rounded-xl text-sm transition-colors whitespace-nowrap">
                <BsPlus size={18} /> Add Video
              </button>
            </div>
          )}

          {project?.videos?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {project.videos.map((url, i) => (
                <div key={i} className="relative rounded-xl overflow-hidden bg-gray-900 aspect-video">
                  <video src={url} controls className="w-full h-full" />
                  <button onClick={() => removeVideo(i)}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow transition-colors">
                    <BsTrash size={11} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-300">
              <BsPlayCircle size={28} className="mx-auto mb-2" />
              <p className="text-sm">No videos yet</p>
            </div>
          )}
        </div>

        {/* ── Existing Photos ── */}
        {pictures.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4">
              Photos ({pictures.length})
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {pictures.map((pic, i) => (
                <div key={i} className="rounded-xl overflow-hidden bg-gray-100 flex flex-col">
                  <div className="aspect-square overflow-hidden">
                    <img src={pic.picture} alt={pic.details || `Photo ${i + 1}`} className="w-full h-full object-cover" />
                  </div>
                  {pic.details ? (
                    <p className="text-gray-600 text-[11px] px-2 py-1.5 leading-tight bg-white border-t border-gray-100">{pic.details}</p>
                  ) : (
                    <p className="text-gray-300 text-[11px] px-2 py-1.5 italic bg-white border-t border-gray-100">No caption</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!loadingProject && pictures.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center text-gray-400">
            <BsImage size={32} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm font-medium">No photos added yet</p>
          </div>
        )}

        {/* ── Add Photo ── */}
        <Form onFinish={submit} className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4">Add New Photo</h2>
            <Form.Item name="picture" style={{ marginBottom: 0 }}>
              <Dragger {...uploadConfig} className="!border-dashed !border-gray-300 !rounded-xl !bg-gray-50 hover:!border-[#1877F2] transition-colors">
                {fileName ? (
                  <div className="py-6 flex flex-col items-center gap-2">
                    <BsCheckCircle size={28} className="text-green-500" />
                    <p className="text-sm font-semibold text-gray-700">{fileName}</p>
                    <p className="text-xs text-gray-400">Click or drag to replace</p>
                  </div>
                ) : (
                  <div className="py-8 flex flex-col items-center gap-2">
                    <BsCloudUpload size={32} className="text-gray-400" />
                    <p className="text-sm font-semibold text-gray-600">Click or drag to upload</p>
                    <p className="text-xs text-gray-400">JPG, PNG, WebP</p>
                  </div>
                )}
              </Dragger>
            </Form.Item>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <label className={labelClass}>Caption <span className="text-gray-400 font-normal">(optional)</span></label>
            <input type="text" name="details" value={formFields.details}
              placeholder="e.g. Foundation work completed" onChange={(e) => setFormFields(p => ({ ...p, details: e.target.value }))}
              className={inputClass} />
          </div>

          <Form.Item style={{ marginBottom: 0 }}>
            <button type="submit" disabled={isSubmitting || !formFields.picture}
              className="bg-[#1877F2] hover:bg-[#0e5fc7] text-white font-bold px-8 py-3 rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
              {isSubmitting ? "Uploading…" : "Add Photo"}
            </button>
          </Form.Item>
        </Form>

      </div>
    </div>
  );
};

export default AddPicture;
